import { Controller } from 'stimulus';

const OFFSET_MARGIN = '500px';
const CLASS_HIDDEN = 'd-none';

export default class extends Controller {
  static targets = ['entries', 'pagination', 'loadMoreText', 'loadingText'];

  initialize() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => this.processIntersectionEntries(entries),
      this.options
    );
    // Bumped by #restart, so a batch already in flight when the list is restarted is
    // dropped rather than appended to the list that replaced it.
    this.generation = 0;
  }

  connect() {
    this.startObserver();
    document.addEventListener('turbo:morph', this.reobserveAfterMorph);
  }

  disconnect() {
    document.removeEventListener('turbo:morph', this.reobserveAfterMorph);
    this.stopObserver();
  }

  // Re-arms the observer after something outside this controller has changed the list --
  // Resident Settings restarts it when a filter changes. Re-observing is what delivers a
  // fresh callback: an observer whose target was already intersecting does not fire again
  // just because that target's contents were replaced.
  restart() {
    this.generation += 1;
    this.toggleLoadingState(false);
    this.stopObserver();
    this.startObserver();
  }

  // A morphing page render preserves this element, so `connect` does not run
  // again — and an IntersectionObserver only reports *transitions*, so a
  // "Load more" that was already in view before the morph never reports being in
  // view after it, and the next page is never fetched. (A replacing render got
  // this for free: a brand new observer always delivers an initial callback.)
  // Re-observing asks for that initial callback again.
  reobserveAfterMorph = () => {
    this.stopObserver();
    this.startObserver();
  };

  startObserver() {
    if (this.hasPaginationTarget) {
      this.intersectionObserver.observe(this.paginationTarget);
    }
  }

  stopObserver() {
    if (this.hasPaginationTarget) {
      this.intersectionObserver.unobserve(this.paginationTarget);
    }
  }

  toggleLoadingState(shouldToggle) {
    // `setAttribute`, not `toggleAttribute`: the markup ships `aria-busy="false"`, and
    // toggling an attribute that is already present leaves its value alone — so the busy
    // state read "false" for the whole fetch and then alternated between absent and empty.
    this.element.setAttribute('aria-busy', String(shouldToggle));

    if (this.hasLoadMoreTextTarget) {
      this.loadMoreTextTarget.classList.toggle(CLASS_HIDDEN, shouldToggle);
      this.loadingTextTarget.classList.toggle(CLASS_HIDDEN, !shouldToggle);
    }
  }

  processIntersectionEntries(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.loadMore();
      }
    });
  }

  async loadMore() {
    let next_page = this.paginationTarget.querySelector("a[rel='next']");
    if (next_page == null) return;

    this.stopObserver();
    this.toggleLoadingState(true);
    const generation = this.generation;

    try {
      let url = next_page.href;
      const response = await fetch(url, {
        headers: {
          Accept: 'application/json',
        },
      });
      const { entries, pagination } = await response.json();

      // The list this batch belonged to was restarted while it was in flight. Appending it
      // now would put back a batch the restart removed, out of the list's order.
      if (generation !== this.generation) return;

      this.entriesTarget.insertAdjacentHTML('beforeend', entries);
      this.paginationTarget.innerHTML = pagination;

      this.startObserver();
      this.toggleLoadingState(false);

      this.element.dispatchEvent(
        new CustomEvent('infinite-scroll.loaded', {
          bubbles: true,
        })
      );
    } catch (error) {
      console.error(error);
      this.startObserver();
      this.toggleLoadingState(false);
    }
  }

  get options() {
    return {
      rootMargin: OFFSET_MARGIN,
    };
  }
}
